import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import {
  faExclamationTriangle,
  faPaperclip,
} from '@fortawesome/free-solid-svg-icons';

import Alert from '@/components/Alert';
import Avatar from '@/components/Avatar';
import Button from '@/components/Button';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import { Body, Heading } from '@/components/Typography';
import Icon from '@/elements/Icon';
import Link from '@/elements/Link';
import { accountabilityAPI } from '@/services/accountabilityAPI';
import styled from '@/styles';
import { ActivityAction, ViolationDetail } from '@/types/accountability';

import ViolationActionModal from '../shared/ViolationActionModal';

const ContentCard = styled('div', {
  backgroundColor: 'white',
  border: '1px solid $neutralLightest',
  borderRadius: '$xxl',
  boxShadow: '$1',
  padding: '24px',
  '@phoneOnly': {
    padding: '16px',
  },
});

const Layout = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
});

const HeaderActions = styled('div', {
  display: 'flex',
  gap: 8,
});

const TopBadges = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
});

const TopBadge = styled('span', {
  alignItems: 'center',
  borderRadius: '$round',
  display: 'inline-flex',
  fontSize: '$lg',
  fontWeight: '$semi',
  lineHeight: 1.2,
  padding: '6px 14px',
  variants: {
    tone: {
      violationLateDrop: {
        backgroundColor: '#F6E8E5',
        color: '#D45508',
      },
      violationNoShow: {
        backgroundColor: '#FDEAEA',
        color: '#C81E1E',
      },
      statusNeedsReview: {
        backgroundColor: '#E97931',
        color: 'white',
      },
      statusOpen: {
        backgroundColor: '#E7F6E7',
        color: '#1D7A35',
      },
      statusResolved: {
        backgroundColor: '#F3F4F6',
        color: '#4B5563',
      },
    },
  },
});

const WorkerRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 20,
  '@phoneOnly': {
    flexDirection: 'column',
  },
});

const WorkerIdentity = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const WorkerText = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const WorkerNameLink = styled(Link, {
  color: '$textDefault',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  textDecoration: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    color: '$themeDark',
    textDecoration: 'underline',
  },
});

const WorkerStats = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 6,
  minWidth: 170,
  '@phoneOnly': {
    alignItems: 'flex-start',
  },
});

const DependabilityText = styled('div', {
  fontSize: '$lg',
  lineHeight: 1.2,
  fontWeight: '$semi',
  letterSpacing: '-0.01em',
  '@phoneOnly': {
    fontSize: '$md',
  },
  variants: {
    severity: {
      high: { color: '#1D7A35' },
      medium: { color: '#D97706' },
      low: { color: '#D20614' },
    },
  },
});

const CountPill = styled('div', {
  border: '1px solid #D0D0D0',
  borderRadius: '$xxl',
  padding: '4px 10px',
  fontSize: '$md',
  color: '$textDefault',
  lineHeight: 1.3,
});

const Section = styled('section', {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const SectionDivider = styled('div', {
  borderTop: '1px solid #E5E7EB',
  width: '100%',
});

const TabsWrapper = styled('div', {
  display: 'flex',
  gap: 24,
  borderBottom: '1px solid #E5E7EB',
  width: '100%',
});

const TabButton = styled('button', {
  background: 'transparent',
  border: 'none',
  borderBottom: '3px solid transparent',
  color: '#4B5563',
  cursor: 'pointer',
  fontSize: '$lg',
  fontWeight: 500,
  lineHeight: 1.2,
  padding: '0 0 12px',
  '@phoneOnly': {
    fontSize: '$md',
  },
  variants: {
    active: {
      true: {
        borderBottomColor: '#4FAF4A',
        color: '#111827',
        fontWeight: 600,
      },
      false: {},
    },
  },
});

const DetailsBlock = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  marginTop: 8,
});

const DetailItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const ExplanationPanel = styled('div', {
  backgroundColor: '$neutralA4',
  border: '1px solid $neutralA12',
  borderRadius: '$xxl',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  marginTop: 4,
  padding: '12px',
});

const ExplanationSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const AttachmentPill = styled('div', {
  alignItems: 'center',
  border: '1px solid $successDefault',
  borderRadius: '$xxl',
  display: 'inline-flex',
  gap: 8,
  padding: '6px 12px',
  backgroundColor: '$successA8',
});

const AuditTrailList = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  marginTop: 8,
});

const AuditTrailRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: '36px 1fr',
  gap: 12,
  alignItems: 'start',
  borderBottom: '1px solid #E5E7EB',
  paddingBottom: 14,
  '&:last-child': {
    borderBottom: 'none',
    paddingBottom: 0,
  },
  '@phoneOnly': {
    gridTemplateColumns: '32px 1fr',
    gap: 10,
  },
});

const AuditSummary = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const AuditActionText = styled('p', {
  color: '$textDefault',
  fontSize: '$lg',
  lineHeight: 1.3,
  margin: 0,
  '@phoneOnly': {
    fontSize: '$md',
  },
});

const AuditTimestamp = styled('div', {
  color: '$textLight',
  fontSize: '$md',
  lineHeight: 1.35,
  '@phoneOnly': {
    fontSize: '$sm',
  },
});

const AuditTransition = styled('div', {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 4,
});

const AuditChip = styled('span', {
  border: '1px solid #D1D5DB',
  borderRadius: '$xl',
  color: '$textDefault',
  display: 'inline-flex',
  fontSize: '$md',
  lineHeight: 1.2,
  padding: '4px 12px',
});

const AuditArrow = styled('span', {
  color: '$textLight',
  fontSize: '$xl',
  lineHeight: 1,
});

const DetailsToggle = styled('button', {
  background: 'transparent',
  border: 'none',
  color: '$themeDefault',
  cursor: 'pointer',
  fontSize: '$md',
  padding: 0,
  textDecoration: 'underline',
  '@phoneOnly': {
    fontSize: '$sm',
  },
});

const AuditDetailsBox = styled('div', {
  backgroundColor: '$neutralA4',
  border: '1px solid $neutralA12',
  borderRadius: '$xl',
  color: '$textDefault',
  fontSize: '$sm',
  lineHeight: 1.45,
  marginTop: 6,
  padding: '8px 10px',
  whiteSpace: 'pre-wrap',
});

const TAB_KEYS = ['details', 'audit'] as const;
type TabKey = (typeof TAB_KEYS)[number];
type ExplanationSectionData = { title: string | null; text: string };
type TopBadgeTone =
  | 'violationLateDrop'
  | 'violationNoShow'
  | 'statusNeedsReview'
  | 'statusOpen'
  | 'statusResolved';
type AuditTransitionData = { from: string; to: string };

const formatShiftDateRange = (startAt?: string, endAt?: string) => {
  if (!startAt || !endAt) return 'N/A';
  const start = new Date(startAt);
  const end = new Date(endAt);
  return `${format(start, 'EEE, MMM d, yyyy')}, ${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
};

const formatViolationDateRange = (occurredAt?: string, shiftEndAt?: string) => {
  if (!occurredAt) return 'N/A';
  const start = new Date(occurredAt);
  const end = new Date(shiftEndAt || occurredAt);
  return `${format(start, 'EEE, MMM d, yyyy, h:mm a')} - ${format(end, 'h:mm a')}`;
};

const getDependabilityMeta = (rating: number, labelFromApi?: string) => {
  const fallbackLabel =
    rating === 1
      ? 'High Dependability'
      : rating === 2
        ? 'Medium Dependability'
        : 'Low Dependability';

  if (rating === 1)
    return {
      label: labelFromApi || fallbackLabel,
      severity: 'high' as const,
    };
  if (rating === 2)
    return {
      label: labelFromApi || fallbackLabel,
      severity: 'medium' as const,
    };
  return {
    label: labelFromApi || fallbackLabel,
    severity: 'low' as const,
  };
};

const getDisplayStatusLabel = (status: string) => {
  const statusMap: Record<string, string> = {
    needs_review: 'Needs Review',
    open: 'Open',
    upheld: 'Upheld',
    cleared: 'Cleared',
  };

  return statusMap[status] || status;
};

const getViolationBadgeTone = (violationType: string): TopBadgeTone => {
  return violationType === 'late_drop'
    ? 'violationLateDrop'
    : 'violationNoShow';
};

const getStatusBadgeTone = (status: string): TopBadgeTone => {
  if (status === 'needs_review') return 'statusNeedsReview';
  if (status === 'open') return 'statusOpen';
  return 'statusResolved';
};

const EXPLANATION_SECTION_REGEX = /^([A-Z][A-Z0-9\s'/-]+):\s*(.*)$/;

const toReadableHeading = (text: string) =>
  text
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const parseExplanationSections = (
  explanation?: string | null,
): ExplanationSectionData[] => {
  if (!explanation) return [];

  return explanation
    .split(/\n{2,}/)
    .map((section) => section.trim())
    .filter(Boolean)
    .map((section) => {
      const match = section.match(EXPLANATION_SECTION_REGEX);

      if (!match) {
        return { title: null, text: section };
      }

      return {
        title: toReadableHeading(match[1]),
        text: match[2].trim(),
      };
    });
};

const getViolationTypeLabel = (type: ViolationDetail['violation_type']) =>
  type === 'late_drop' ? 'Late Drop' : 'No Show';

const getAuditActor = (action: ActivityAction, workerName: string) => {
  if (action.performed_by_type === 'system') {
    return { name: 'GravyWork', role: 'System' };
  }

  if (action.performed_by_type === 'worker') {
    return { name: workerName, role: 'GravyWorker' };
  }

  return {
    name: action.performed_by_name || 'Admin',
    role: action.performed_by_role || 'Admin',
  };
};

const buildAuditMessage = (
  action: ActivityAction,
  workerName: string,
  violationType: ViolationDetail['violation_type'],
) => {
  const actor = getAuditActor(action, workerName);

  switch (action.action_type) {
    case 'appeal_submitted':
      return `${actor.name} (${actor.role}) submitted an appeal.`;
    case 'violation_created':
      return `${actor.name} (${actor.role}) tagged ${workerName} with ${getViolationTypeLabel(violationType)} violation.`;
    case 'suspension_applied':
      return `${actor.name} (${actor.role}) suspended ${workerName}'s account.`;
    case 'appeal_approved':
      return `${actor.name} (${actor.role}) approved the appeal and cleared the violation.`;
    case 'appeal_denied':
      return `${actor.name} (${actor.role}) denied the appeal.`;
    case 'violation_upheld':
      return `${actor.name} (${actor.role}) upheld the violation.`;
    case 'violation_forgiven':
      return `${actor.name} (${actor.role}) cleared the violation.`;
    case 'violation_removed':
      return `${actor.name} (${actor.role}) removed the violation.`;
    case 'suspension_lifted':
      return `${actor.name} (${actor.role}) lifted the suspension.`;
    case 'suspension_completed':
      return `${actor.name} (${actor.role}) marked the suspension as completed.`;
    default:
      return `${actor.name} (${actor.role}) ${action.action_label.toLowerCase()}.`;
  }
};

const getAuditTransition = (
  action: ActivityAction,
  violationType: ViolationDetail['violation_type'],
): AuditTransitionData | null => {
  switch (action.action_type) {
    case 'violation_created':
      return { from: 'No Violation', to: getViolationTypeLabel(violationType) };
    case 'appeal_submitted':
      return { from: 'No Appeal', to: 'Appeal Submitted' };
    case 'appeal_approved':
      return { from: 'Needs Review', to: 'Cleared' };
    case 'appeal_denied':
      return { from: 'Needs Review', to: 'Upheld' };
    case 'violation_upheld':
      return { from: 'Open', to: 'Upheld' };
    case 'violation_forgiven':
      return { from: 'Open', to: 'Cleared' };
    case 'violation_removed':
      return { from: 'Violation', to: 'Removed' };
    case 'suspension_applied':
      return { from: 'Account Active', to: 'Account Suspended' };
    case 'suspension_lifted':
    case 'suspension_completed':
      return { from: 'Account Suspended', to: 'Account Active' };
    default:
      return null;
  }
};

const formatAuditDetails = (action: ActivityAction) => {
  const metadata = action.metadata || {};
  const metadataKeys = Object.keys(metadata);
  const metadataText =
    metadataKeys.length > 0 ? JSON.stringify(metadata, null, 2) : '';
  const reasonText = action.reason?.trim() || '';

  if (!reasonText && !metadataText) return '';
  if (reasonText && metadataText) return `${reasonText}\n\n${metadataText}`;
  return reasonText || metadataText;
};

export const ViolationDetailPage: React.FC = () => {
  const { violationId } = useParams<{ violationId: string }>();
  const navigate = useNavigate();

  const [violation, setViolation] = useState<ViolationDetail | null>(null);
  const [activityActions, setActivityActions] = useState<ActivityAction[]>([]);
  const [expandedActionIds, setExpandedActionIds] = useState<
    Record<number, boolean>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('details');
  const [canClearViolation, setCanClearViolation] = useState(false);
  const [actionModal, setActionModal] = useState<{
    type: 'uphold' | 'clear';
  } | null>(null);

  useEffect(() => {
    const id = Number(violationId);
    if (!violationId || Number.isNaN(id)) {
      setError('Invalid violation ID');
      setLoading(false);
      return;
    }

    loadViolationData(id);
  }, [violationId]);

  useEffect(() => {
    let isMounted = true;

    const loadClearPermission = async () => {
      try {
        const response = await accountabilityAPI.getClearPermission();
        if (!isMounted) return;
        setCanClearViolation(
          !!(response.success && response.can_clear_violation),
        );
      } catch (err) {
        if (isMounted) {
          setCanClearViolation(false);
        }
      }
    };

    loadClearPermission();

    return () => {
      isMounted = false;
    };
  }, []);

  const loadViolationData = async (id: number) => {
    setLoading(true);
    setError(null);

    try {
      const [detailResult, activityResult] = await Promise.allSettled([
        accountabilityAPI.getViolationDetail(id),
        accountabilityAPI.getViolationActivity(id),
      ]);

      if (detailResult.status === 'fulfilled' && detailResult.value.success) {
        setViolation(detailResult.value.violation);
      } else {
        setError('Failed to load violation details');
        return;
      }

      if (
        activityResult.status === 'fulfilled' &&
        activityResult.value.success
      ) {
        setActivityActions(activityResult.value.actions);
      } else {
        setActivityActions([]);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load violation');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Page
        breadcrumbs={[
          { a11yLabel: 'Tasks', label: 'Tasks', to: '/tasks' },
          {
            a11yLabel: 'Violations',
            label: 'Violations',
            to: '/tasks/violations',
          },
          { a11yLabel: 'Review Violation', label: 'Review Violation' },
        ]}
        size="full"
        title="Review Violation"
      >
        <LoadingState text="Loading violation details..." />
      </Page>
    );
  }

  if (error || !violation) {
    return (
      <Page
        breadcrumbs={[
          { a11yLabel: 'Tasks', label: 'Tasks', to: '/tasks' },
          {
            a11yLabel: 'Violations',
            label: 'Violations',
            to: '/tasks/violations',
          },
          { a11yLabel: 'Review Violation', label: 'Review Violation' },
        ]}
        size="full"
        title="Review Violation"
      >
        <Alert
          description={error || 'Violation not found'}
          icon={faExclamationTriangle}
          status="danger"
          title="Error"
        />
      </Page>
    );
  }

  const canTakeAction =
    violation.display_status === 'open' ||
    violation.display_status === 'needs_review';

  const [firstName, ...restName] = (violation.worker.name || '')
    .trim()
    .split(/\s+/);
  const lastName = restName.join(' ');
  const dependability = getDependabilityMeta(
    violation.worker.dependability_rating,
    violation.worker.dependability_label,
  );
  const violationCount =
    violation.violation_type === 'late_drop'
      ? violation.worker.jobs_dropped
      : violation.worker.no_show;
  const violationCountLabel =
    violation.violation_type === 'late_drop' ? 'Late Drop' : 'No Show';
  const explanationSections = parseExplanationSections(violation.explanation);
  const toggleActionDetails = (actionId: number) =>
    setExpandedActionIds((prev) => ({ ...prev, [actionId]: !prev[actionId] }));

  return (
    <Page
      breadcrumbs={[
        { a11yLabel: 'Tasks', label: 'Tasks', to: '/tasks' },
        {
          a11yLabel: 'Violations',
          label: 'Violations',
          to: '/tasks/violations',
        },
        { a11yLabel: 'Review Violation', label: 'Review Violation' },
      ]}
      customActions={
        canTakeAction ? (
          <HeaderActions>
            <Button
              a11yLabel="Uphold violation"
              appearance="outline"
              css={{
                backgroundColor: 'white',
                borderColor: '#9A9A9A',
                borderRadius: '$xxl',
                color: '#636363',
                fontSize: '$lg',
                fontWeight: '$medium',
                height: 40,
                minWidth: 132,
                padding: '0 18px',
              }}
              label="Uphold"
              onClick={() => setActionModal({ type: 'uphold' })}
            />
            {canClearViolation && (
              <Button
                a11yLabel="Clear violation"
                css={{
                  backgroundColor: '#43A936',
                  borderColor: '#43A936',
                  borderRadius: '$xxl',
                  boxShadow: '0 4px 12px rgba(67, 169, 54, 0.22)',
                  fontSize: '$lg',
                  fontWeight: '$semi',
                  height: 40,
                  minWidth: 132,
                  padding: '0 18px',
                }}
                label="Clear"
                status="success"
                onClick={() => setActionModal({ type: 'clear' })}
              />
            )}
          </HeaderActions>
        ) : null
      }
      size="full"
      title="Review Violation"
    >
      {actionModal && (
        <ViolationActionModal
          type={actionModal.type}
          violationId={violation.id}
          workerName={violation.worker.name}
          onClose={() => setActionModal(null)}
          onSuccess={() => navigate('/tasks/violations')}
        />
      )}

      <ContentCard>
        <Layout>
          <TopBadges>
            <TopBadge tone={getViolationBadgeTone(violation.violation_type)}>
              {violation.violation_type === 'late_drop'
                ? 'Late Drop'
                : 'No Show'}
            </TopBadge>
            <TopBadge tone={getStatusBadgeTone(violation.display_status)}>
              {getDisplayStatusLabel(violation.display_status)}
            </TopBadge>
          </TopBadges>

          <WorkerRow>
            <WorkerIdentity>
              <Avatar
                firstName={firstName}
                lastName={lastName}
                size="lg"
                src={violation.worker.avatar_url || undefined}
              />
              <WorkerText>
                <Heading>
                  <WorkerNameLink to={`/workers_v2/${violation.worker.id}`}>
                    {violation.worker.name}
                  </WorkerNameLink>
                </Heading>
                <Body color="light">
                  {violation.worker.email} | {violation.worker.phone}
                </Body>
              </WorkerText>
            </WorkerIdentity>

            <WorkerStats>
              <DependabilityText severity={dependability.severity}>
                {dependability.label}
              </DependabilityText>
              <CountPill>
                {violationCount} {violationCountLabel}
                {violationCount !== 1 ? 's' : ''}
              </CountPill>
            </WorkerStats>
          </WorkerRow>

          <Section>
            <Heading size="lg">Shift details</Heading>
            <SectionDivider />
            <Body css={{ color: '$themeDefault' }} size="lg" weight="medium">
              <Link
                to={`/orders/${violation.job.order_id}/jobs/${violation.job.id}`}
              >
                Shift ID #{violation.job.id}
              </Link>
            </Body>
            <Body size="lg" weight="medium">
              {violation.job.skill_name}
            </Body>
            <Body>
              {formatShiftDateRange(
                violation.job.first_shift_start_at,
                violation.job.last_shift_end_at,
              )}
            </Body>
            {violation.job.address.venue_name && (
              <Body color="light">{violation.job.address.venue_name}</Body>
            )}
            {violation.job.address.line1 && (
              <Body color="light">{violation.job.address.line1}</Body>
            )}
            {violation.job.address.line2 && (
              <Body color="light">{violation.job.address.line2}</Body>
            )}
            <Body color="light">
              {[
                violation.job.address.city,
                violation.job.address.state,
                violation.job.address.zip,
              ]
                .filter(Boolean)
                .join(', ')}
            </Body>
          </Section>

          <Section>
            <TabsWrapper>
              <TabButton
                active={activeTab === 'details'}
                onClick={() => setActiveTab('details')}
                type="button"
              >
                Violation details
              </TabButton>
              <TabButton
                active={activeTab === 'audit'}
                onClick={() => setActiveTab('audit')}
                type="button"
              >
                Audit Trail
              </TabButton>
            </TabsWrapper>

            {activeTab === 'details' && (
              <DetailsBlock>
                {violation.resolution && (
                  <>
                    <DetailItem>
                      <Body weight="medium">Status</Body>
                      <Body>
                        {violation.resolution.action_type ===
                          'violation_upheld' ||
                        violation.resolution.action_type === 'appeal_denied'
                          ? 'Violation Upheld'
                          : 'Violation Cleared'}
                      </Body>
                    </DetailItem>
                    <DetailItem>
                      <Body weight="medium">Updated by</Body>
                      <Body>
                        {violation.resolution.performed_by_name} -{' '}
                        {violation.resolution.performed_by_role}
                      </Body>
                    </DetailItem>
                    <DetailItem>
                      <Body weight="medium">Timestamp</Body>
                      <Body>
                        {format(
                          new Date(violation.resolution.performed_at),
                          'MMM d, yyyy, h:mm a',
                        )}
                      </Body>
                    </DetailItem>
                    <DetailItem>
                      <Body weight="medium">Reason</Body>
                      <Body>
                        {violation.resolution.reason || 'No reason provided'}
                      </Body>
                    </DetailItem>
                  </>
                )}
                <DetailItem>
                  <Body weight="medium">Date Occured</Body>
                  <Body>
                    {formatViolationDateRange(
                      violation.occurred_at,
                      violation.job.last_shift_end_at,
                    )}
                  </Body>
                </DetailItem>
                <DetailItem>
                  <Body weight="medium">Hours before the violation</Body>
                  <Body>{violation.hours_before_shift} hr</Body>
                </DetailItem>
                <DetailItem>
                  <Body weight="medium">
                    {violation.violation_type === 'late_drop'
                      ? 'Late Drop Reason'
                      : 'No Show Reason'}
                  </Body>
                  {explanationSections.length > 0 ? (
                    <ExplanationPanel>
                      {explanationSections.map((section, index) => (
                        <ExplanationSection
                          key={`${section.title || 'section'}-${index}`}
                        >
                          {section.title && (
                            <Body weight="medium">{section.title}</Body>
                          )}
                          {section.text && <Body>{section.text}</Body>}
                        </ExplanationSection>
                      ))}
                    </ExplanationPanel>
                  ) : (
                    <Body>No reason provided</Body>
                  )}
                </DetailItem>
                {violation.appeal_status === 'pending' && (
                  <DetailItem>
                    <Body weight="medium">Submitted Appeal</Body>
                    <Body>
                      {violation.appeal_reason || 'No appeal reason provided'}
                    </Body>
                  </DetailItem>
                )}
                {violation.file_attachment_url && (
                  <Link target="_blank" to={violation.file_attachment_url}>
                    <AttachmentPill>
                      <Icon icon={faPaperclip} size="sm" theme="theme" />
                      <Body
                        css={{ color: '$themeDefault', margin: 0 }}
                        weight="medium"
                      >
                        {violation.file_attachment_name ||
                          'Medical certificate.pdf'}
                      </Body>
                    </AttachmentPill>
                  </Link>
                )}
              </DetailsBlock>
            )}

            {activeTab === 'audit' && (
              <AuditTrailList>
                {activityActions.length === 0 && (
                  <Body color="light">No audit activity available</Body>
                )}
                {activityActions.map((action) => {
                  const auditMessage = buildAuditMessage(
                    action,
                    violation.worker.name,
                    violation.violation_type,
                  );
                  const transition = getAuditTransition(
                    action,
                    violation.violation_type,
                  );
                  const detailText = formatAuditDetails(action);
                  const isExpanded = Boolean(expandedActionIds[action.id]);
                  const showDetailsToggle = detailText.length > 0;
                  const actor = getAuditActor(action, violation.worker.name);

                  return (
                    <AuditTrailRow key={action.id}>
                      {action.performed_by_type === 'worker' ? (
                        <Avatar
                          firstName={firstName}
                          lastName={lastName}
                          rounded
                          size="sm"
                          src={violation.worker.avatar_url || undefined}
                        />
                      ) : (
                        <Avatar
                          firstName={actor.name.split(' ')[0] || 'G'}
                          initialsFormat="first"
                          rounded
                          size="sm"
                          variant="neutral"
                        />
                      )}
                      <AuditSummary>
                        <AuditActionText>{auditMessage}</AuditActionText>
                        <AuditTimestamp>
                          {format(
                            new Date(action.created_at),
                            'MMM d, yyyy, h:mm a',
                          )}
                        </AuditTimestamp>

                        {transition && (
                          <AuditTransition>
                            <AuditChip>{transition.from}</AuditChip>
                            <AuditArrow>&rarr;</AuditArrow>
                            <AuditChip>{transition.to}</AuditChip>
                          </AuditTransition>
                        )}

                        {showDetailsToggle && (
                          <>
                            <DetailsToggle
                              type="button"
                              onClick={() => toggleActionDetails(action.id)}
                            >
                              {isExpanded ? 'Hide details' : 'View details'}
                            </DetailsToggle>
                            {isExpanded && (
                              <AuditDetailsBox>{detailText}</AuditDetailsBox>
                            )}
                          </>
                        )}
                      </AuditSummary>
                    </AuditTrailRow>
                  );
                })}
              </AuditTrailList>
            )}
          </Section>
        </Layout>
      </ContentCard>
    </Page>
  );
};

export default ViolationDetailPage;
